// Componente de Login para la aplicación Amarsi Control Panel
// Maneja el formulario de autenticación con estilos consistentes con RegisterScreen

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LayoutSignUp from '../../components/UI/LayoutSignUp';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import PasswordInput from '../../components/UI/PasswordInput';

/**
 * Componente Login
 * 
 * Formulario de autenticación con email y contraseña
 * Incluye validación, mensajes de error y animaciones de carga
 */
const Login = () => {
  // Estado del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  
  // Estados para validación y UI
  const [errors, setErrors] = useState({});
  
  // Refs para inputs
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Hook de autenticación
  const { login, isLoggingIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Efecto para manejar redirección después del login exitoso
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔍 Login - Usuario autenticado, verificando redirecciones...');
      
      // 1. Verificar si hay URL de retorno de Mercado Pago guardada
      const mpReturnUrl = sessionStorage.getItem('mp_return_url');
      if (mpReturnUrl) {
        console.log('🔄 Redirigiendo a URL de Mercado Pago guardada:', mpReturnUrl);
        sessionStorage.removeItem('mp_return_url'); // Limpiar después de usar
        window.location.href = mpReturnUrl;
        return;
      }

      // 2. Verificar si hay estado de redirección de React Router
      const state = location.state;
      if (state?.from) {
        console.log('🔄 Redirigiendo a estado guardado:', state.from);
        navigate(state.from, { replace: true });
        return;
      }

      // 3. Redirección por defecto
      console.log('🔄 Redirigiendo a /premium por defecto');
      navigate('/premium', { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Efecto para cargar preferencias guardadas al montar el componente
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberMePreference = localStorage.getItem('rememberMe') === 'true';
    
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: rememberMePreference,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        rememberMe: rememberMePreference,
      }));
    }
  }, []);

  // Maneja cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Maneja checkboxes y otros inputs de manera diferente
    const fieldValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue,
    }));
    
    // Limpia errores del campo cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Valida el formulario antes de enviar
  const validateForm = () => {
    const newErrors = {};

    // Validación de email
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    // Validación de contraseña
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Maneja el envío del formulario
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await login(formData.email, formData.password, formData.rememberMe);
      // El manejo de éxito se hace en el contexto y mutación
    } catch (error) {
      // El error ya es manejado por la mutación, pero podemos agregar lógica adicional
      console.error('Error en login:', error);
    }
  };


  return (
    <LayoutSignUp withProgressBar={false}>
      <div className="p-6">
        <div className="text-center mb-6">
          <AmarsiLogo width="80" height="80" className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Iniciá sesión
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">
            ¡Qué bueno volver a verte! 😊
          </p>
        </div>

        <div>

          <div className='space-y-4'>
            {/* Campo Email */}
            <Input
              ref={emailInputRef}
              type="email"
              label="Correo electrónico"
              placeholder="Tu correo electrónico"
              value={formData.email}
              onChange={(e) => handleChange({ target: { name: 'email', value: e.target.value } })}
              error={errors.email}
              icon="email"
              autoFocus
            />

            {/* Campo Contraseña */}
            <PasswordInput
              ref={passwordInputRef}
              label="Contraseña"
              placeholder="Tu contraseña"
              value={formData.password}
              onChange={(e) => handleChange({ target: { name: 'password', value: e.target.value } })}
              error={errors.password}
            />
          </div>

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              onClick={() => navigate('/auth/forgot-password')}
              className="text-sm dark:text-gray-200 hover:text-primary-500 font-medium"
            >
              ¿Olvidaste tu contraseña?
            </button>

            {/* Opción de recordarme */}
            <div className="flex items-center justify-end">
              <input
                id="remember-me"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => handleChange({ target: { name: 'rememberMe', value: e.target.checked, type: 'checkbox', checked: e.target.checked } })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                Recordarme
              </label>
            </div>

          </div>
        </div>
      </div>

      <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700">
        <div className="text-center">
          <Button
            onClick={handleSubmit}
            loading={isLoggingIn}
            disabled={!formData.email || formData.password.length < 6}
            size='md'
            className='w-full'
            variant='primary'
          >
            Iniciar sesión
          </Button>
        </div>
        
        <div className="text-center mt-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            ¿No tenés cuenta?{' '}
            <button 
              onClick={() => navigate('/auth/register')}
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              Creá una ahora
            </button>
          </p>
        </div>
      </div>
    </LayoutSignUp>
  );
};

export default Login;
